import { SCORING } from '../constants';

/**
 * Calculates points awarded for a year guess based on accuracy.
 * 
 * The scoring system is designed around a few key principles:
 * 1. Perfect guesses get maximum points (1000)
 * 2. Close guesses (1-5 years off) use a gentle cubic curve to avoid harsh penalties 
 * 3. More distant guesses face exponential decay
 * 4. Very poor guesses (50+ years off) get zero points
 * 
 * The algorithm also accounts for the fact that guessing years for more recent photos
 * tends to be easier than for older historical photos, so the scoring curve is slightly
 * more forgiving for recent decades.
 * 
 * Mathematical approach:
 * - For small errors (≤5 years): Uses a custom cubic polynomial to create a gentle falloff
 * - For larger errors: Applies exponential decay with year-adjusted scaling
 * - The base (0.752) and scaling factors were empirically tuned to create a satisfying
 *   risk/reward balance where players are incentivized to guess confidently but not
 *   penalized too harshly for reasonable mistakes.
 */
export const calculatePoints = (
  guessedYear: number,
  actualYear: number
): number => {
  const diff = Math.abs(guessedYear - actualYear);

  // Perfect guess
  if (diff === 0) return SCORING.PERFECT_SCORE;

  // No points for very poor guesses
  if (diff >= SCORING.MAX_PENALTY_THRESHOLD) return 0;

  // Adjust difficulty scaling based on how recent the photo is
  // Photos from recent decades are generally easier to date, so we're slightly more generous
  const yearsSince1900 = actualYear - SCORING.REFERENCE_YEAR;
  const base = SCORING.CURVE_STEEPNESS; // 0.752 - controls how quickly points decay
  
  // Scale decreases for more recent years, ensuring it doesn't drop below 3
  // This makes the scoring curve slightly more forgiving for modern photos
  const scale = Math.max(
    SCORING.BASE_SCALE,
    SCORING.SCALE_FACTOR - SCORING.SCALE_COEFFICIENT * yearsSince1900
  );

  let effectiveDiff: number;
  if (diff <= 5) {
    // For small errors, use a cubic polynomial to create a gentle curve
    // This prevents harsh penalties for being off by just a few years
    // The coefficients were chosen to make 1-year errors quite forgiving
    effectiveDiff =
      SCORING.POLY_COEFF_CUBIC * Math.pow(diff, 3) +
      SCORING.POLY_COEFF_QUADRATIC * Math.pow(diff, 2) +
      SCORING.POLY_COEFF_LINEAR * diff;
  } else {
    // For larger errors, switch to linear scaling to maintain predictable decay
    effectiveDiff = diff - SCORING.ADJUSTMENT_FACTOR;
  }

  // Apply exponential decay: score = 1000 * (0.752)^(effectiveDiff/scale)
  return Math.round(
    SCORING.PERFECT_SCORE * Math.pow(base, effectiveDiff / scale)
  );
};
